import React from 'react';
import DataCard from '../common/DataCard';
import Badge from '../common/Badge';
import IconButton from '../common/IconButton';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import {
    CompetitionClassDTO,
    CompetitionDTO,
    CompetitionEntryDTO,
    CompetitionStatusDTO,
} from '../../services/apiClient';
import { Crosshair, DollarSign, Info, Target, CheckCircle2 } from 'lucide-react';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors } from '../../utils/displayUtils';
import { CompetitionStatusConstants } from '../../constants/CompetitionStatusConstants';
import { PaymentTypeConstants } from '../../constants/PaymentTypeConstants';

interface CompetitionEntryDataCardProps {
    entries: CompetitionEntryDTO[];
    competitions: CompetitionDTO[];
    competitionClasses: CompetitionClassDTO[];
    competitionStatuses: CompetitionStatusDTO[];
    submittedEntryIds?: Set<string>;
    onMakePayment: (entry: CompetitionEntryDTO) => void;
    onSubmitScore: (entry: CompetitionEntryDTO) => void;
    onDownloadTargets: (entry: CompetitionEntryDTO) => void;
    isLoading: boolean;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
}

const CompetitionEntryDataCard: React.FC<CompetitionEntryDataCardProps> = ({
    entries,
    competitions,
    competitionClasses,
    competitionStatuses,
    submittedEntryIds = new Set(),
    onMakePayment,
    onSubmitScore,
    onDownloadTargets,
    isLoading,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
}) => {
    const getCompetition = (entry: CompetitionEntryDTO) =>
        competitions.find((c) => c.id?.toLowerCase() === entry.competitionId?.toLowerCase());

    const getCompetitionClass = (entry: CompetitionEntryDTO) =>
        competitionClasses.find((c) => c.id?.toLowerCase() === entry.competitionClassId?.toLowerCase());

    const getStatus = (entry: CompetitionEntryDTO) =>
        competitionStatuses.find((s) => s.id?.toLowerCase() === entry.competitionStatusId?.toLowerCase());

    const formatDate = (date: Date | string | undefined) =>
        date ? convertToUserTime(String(date), true) : 'N/A';

    const isTargetDownloadActive = (competition: CompetitionDTO | undefined): boolean => {
        if (!competition?.targetDownloadOpenDate || !competition?.targetDownloadCloseDate) return false;
        const now = new Date();
        return now >= new Date(competition.targetDownloadOpenDate) && now <= new Date(competition.targetDownloadCloseDate);
    };

    const isApproved = (entry: CompetitionEntryDTO): boolean =>
        entry.competitionStatusId?.toLowerCase() === CompetitionStatusConstants.APPROVED;

    const isCompetitionOpen = (competition: CompetitionDTO | undefined): boolean => {
        if (!competition?.startDate || !competition?.endDate) return false;
        const now = new Date();
        return now >= new Date(competition.startDate) && now <= new Date(competition.endDate);
    };

    const isPending = (entry: CompetitionEntryDTO): boolean =>
        entry.competitionStatusId?.toLowerCase() === CompetitionStatusConstants.PENDING;

    // Reads the real recorded payment method rather than guessing from whether a proof file
    // was uploaded - an admin can confirm an EFT payment without one ever being uploaded (e.g.
    // manual confirmation or bank-statement reconciliation), so absence of a proof file does
    // not mean the payment was by card.
    const getPaymentMethodLabel = (entry: CompetitionEntryDTO): string | null => {
        const paymentMethodId = entry.paymentMethodId?.toLowerCase();
        if (paymentMethodId === PaymentTypeConstants.EFT) return 'EFT';
        if (paymentMethodId === PaymentTypeConstants.CARD) return 'Card';
        return null;
    };

    const statusBadge = (entry: CompetitionEntryDTO) => {
        const status = getStatus(entry);
        const base = status?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        let title = status?.title ?? '';
        if (isPending(entry) && entry.proofOfPaymentFileId) {
            title = `${title}\n(Proof Uploaded)`;
        } else if (isApproved(entry)) {
            const paymentMethodLabel = getPaymentMethodLabel(entry);
            title = `${title}\n(Payment Confirmed)${paymentMethodLabel ? `\n(${paymentMethodLabel})` : ''}`;
        }

        return (
            <Badge
                label={title}
                variant="custom"
                customBgColor={bg}
                customTextColor={text}
                className="whitespace-pre-line text-left leading-tight"
            />
        );
    };

    if (isLoading) {
        return (
            <div className="py-8 flex justify-center">
                <Loader />
            </div>
        );
    }

    if (entries.length === 0) {
        return (
            <div className="py-8 text-center text-sm text-gray-500">
                No competition entries found.
            </div>
        );
    }

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {entries.map((entry) => {
                    const competition = getCompetition(entry);
                    const competitionClass = getCompetitionClass(entry);
                    const status = getStatus(entry);

                    return (
                        <DataCard<CompetitionEntryDTO>
                            key={entry.id}
                            item={entry}
                            leftBadgeNode={() => statusBadge(entry)}
                            rightMeta={() => ({
                                label: 'Applied',
                                value: formatDate(entry.createdUtc),
                            })}
                            title={() => competition?.name ?? 'Competition'}
                            fields={[
                                {
                                    label: 'Year',
                                    render: () => String(entry.year ?? ''),
                                },
                                {
                                    label: 'Registration Open',
                                    render: () => formatDate(competition?.registrationOpenDate),
                                },
                                {
                                    label: 'Registration Close',
                                    render: () => formatDate(competition?.registrationCloseDate),
                                },
                                {
                                    label: 'Competition Start',
                                    render: () => formatDate(competition?.startDate),
                                },
                                {
                                    label: 'Competition End',
                                    render: () => formatDate(competition?.endDate),
                                },
                                {
                                    label: 'Class',
                                    render: () => competitionClass?.title ?? 'N/A',
                                    hideIf: (row) => !row.competitionClassId,
                                },
                                {
                                    label: 'Age',
                                    render: () => String(entry.age ?? ''),
                                },
                                {
                                    label: 'Reference',
                                    render: () => entry.reference ?? 'N/A',
                                },
                                {
                                    label: 'Target Download Open',
                                    render: () => formatDate(competition?.targetDownloadOpenDate),
                                    hideIf: () => !competition?.targetDownloadOpenDate,
                                },
                                {
                                    label: 'Target Download Close',
                                    render: () => formatDate(competition?.targetDownloadCloseDate),
                                    hideIf: () => !competition?.targetDownloadCloseDate,
                                },
                            ]}
                            files={[]}
                            primaryActions={(row) => (
                                <>
                                    {competition?.rulesTargetUrl && (
                                        <IconButton
                                            color="blue"
                                            icon={<Info />}
                                            onClick={() => window.open(competition.rulesTargetUrl!, '_blank')}
                                            label="View Competition Rules"
                                        />
                                    )}
                                    {competition && isApproved(row) && isTargetDownloadActive(competition) && (
                                        <IconButton
                                            color="orange"
                                            icon={<Target />}
                                            onClick={() => onDownloadTargets(row)}
                                            label="Download Competition Targets"
                                        />
                                    )}
                                    {isApproved(row) && isCompetitionOpen(competition) && !submittedEntryIds.has(row.id ?? '') && (
                                        <IconButton
                                            color="green"
                                            icon={<Crosshair />}
                                            onClick={() => onSubmitScore(row)}
                                            label="Submit your competition scores"
                                        />
                                    )}
                                    {submittedEntryIds.has(row.id ?? '') && (
                                        <IconButton
                                            color="green"
                                            icon={<CheckCircle2 />}
                                            disabled
                                            label="Score submitted"
                                        />
                                    )}
                                    {!row.isPaymentConfirmed && !row.proofOfPaymentFileId && (
                                        <IconButton
                                            color="green"
                                            icon={<DollarSign />}
                                            onClick={() => onMakePayment(row)}
                                            label="Complete your payment here"
                                        />
                                    )}
                                </>
                            )}
                        />
                    );
                })}
            </div>

            {!isLoading && entries.length > 0 && (
                <div className="mt-4">
                    <Pagination
                        currentPage={Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1}
                        totalPages={Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1}
                        onPageChange={setCurrentPage}
                        pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
                        onPageSizeChange={setPageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default CompetitionEntryDataCard;
